import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PlaceIcon from "@mui/icons-material/Place";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useUser } from "../../users/providers/UserProvider";
import { updateLocationPrivacy, getLocationPrivacy } from "../../users/service/userApi";
import { useAnalytics } from "../../analytics/useAnalytics";
import { fetchFirstTradeMission, stepCopyFor } from "./firstTradeMission";
import { useNavigate } from "react-router-dom";
import { useDismissible } from "../../native/dismissibleStack";
import { useOptionalSnack } from "../../providers/SnackBarProvider";

const FLAG_PREFIX = "cardonomer_onboarded_";
const flagKey = (userId) => `${FLAG_PREFIX}${userId}`;

const hasOnboarded = (userId) => {
  try {
    return !!localStorage.getItem(flagKey(userId));
  } catch {
    return true; // storage blocked → don't nag
  }
};

const markOnboarded = (userId) => {
  try {
    localStorage.setItem(flagKey(userId), new Date().toISOString());
  } catch {
    /* ignore */
  }
};

// Onboarding offers the three privacy modes that need no extra step. "Locked"
// is intentionally omitted here — it requires manually dropping a pin on the
// Map, which can't be done mid-onboarding. Default is the safety-first "Fuzzy".
const PRIVACY_CHOICES = [
  {
    value: "radius",
    label: "Fuzzy",
    desc: "Shown somewhere within about 1 km of you. Recommended.",
    Icon: BlurOnIcon,
    color: "#6b21a8",
  },
  {
    value: "precise",
    label: "Precise",
    desc: "Shown within about 150 m of you, never your exact spot.",
    Icon: GpsFixedIcon,
    color: "#22c55e",
  },
  {
    value: "hidden",
    label: "Hidden",
    desc: "Don't show my location to anyone.",
    Icon: VisibilityOffIcon,
    color: "#ef4444",
  },
];

const SAFETY_TIPS = [
  "Meet in a public place, in daylight.",
  "Bring a friend when you can.",
  "Inspect cards before you hand yours over.",
  "Keep all chat inside the app.",
  "Never share your home address.",
];

const OnboardingCoach = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [privacy, setPrivacy] = useState("radius");
  // The picker's "radius" is a DEFAULT, not the account's stored mode — the
  // coach never reads it. The onboarded flag is per-device localStorage, so an
  // existing collector on a second device replays this whole flow; saving an
  // untouched picker would overwrite a stored "hidden" and put them back on the
  // map. Only an explicit tap is a choice worth writing. (#5)
  const [privacyTouched, setPrivacyTouched] = useState(false);
  // E2E audit E-03. The account's STORED mode, read from the server when the
  // coach opens, so the question "How should others see your location?" shows
  // the true current answer instead of none. Shown, never written: an
  // untouched picker still saves nothing.
  const [storedMode, setStoredMode] = useState(null);
  const [saving, setSaving] = useState(false);
  // The same fact as `saving`, held where the exits can read it WITHIN a tick.
  // `saving` is state: it is not visible to a handler that runs before React
  // re-renders, and Android Back is exactly that handler — `dismissTopDismissible`
  // deliberately does not pop the entry it fires ("Removal is the registering
  // component's job"), so a second Back re-invokes this same `skip` while `open`
  // is still true. A ref is the only reading of "a save is in flight" that is
  // true the instant the save starts. (#261)
  const savingRef = useRef(false);
  const [mission, setMission] = useState(null);

  const userId = user?._id;
  const nextCopy = mission?.nextStepId ? stepCopyFor(mission, mission.nextStepId) : null;

  useEffect(() => {
    if (userId && !hasOnboarded(userId)) {
      setStep(0);
      setPrivacy("radius");
      setPrivacyTouched(false);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => getLocationPrivacy())
      .then((p) => { if (!cancelled) setStoredMode(typeof p?.mode === "string" ? p.mode : null); })
      .catch(() => { if (!cancelled) setStoredMode(null); });
    return () => { cancelled = true; };
  }, [open]);

  const firstName = useMemo(() => user?.name?.first || "there", [user]);

  // Live mission state, so the last step shows what this account has actually
  // done rather than teaching steps it finished weeks ago. Fetched when the
  // coach opens; a failure just hides the step rather than blocking the flow.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchFirstTradeMission()
      .then((m) => {
        if (!cancelled) setMission(m);
      })
      .catch(() => {
        if (!cancelled) setMission(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // The one place the picker's choice is written, shared by every exit.
  //
  // `privacyTouched` is the load-bearing half: without it, completing a
  // replayed coach PUTs the default over whatever the account already had. (#5)
  //
  // Returns whether onboarding may be marked done. An untouched picker is not a
  // choice, so there is nothing to lose and nothing to retry — that is `true`.
  // A save the user explicitly asked for that FAILED is `false`: the previous
  // "non-blocking, the backend default is already the safe radius" defence is
  // fair for Fuzzy and Precise and false for "Hidden — Don't show my location
  // to anyone", because radius mode publishes a jittered point. Reporting the
  // failure is what keeps the onboarded flag unwritten, so the coach comes back
  // and the choice can be made again, instead of being lost forever. (#11)
  const persistPrivacy = async () => {
    if (!privacyTouched) return true;
    savingRef.current = true;
    setSaving(true);
    try {
      await updateLocationPrivacy({ mode: privacy });
      return true;
    } catch {
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Reports whether the exit actually RAN — NOT whether the privacy PUT
  // succeeded. Callers that do something after the exit (the mission CTA
  // navigates) need to know the difference between "this exit was refused and
  // nothing happened" and "this exit completed". Every path that reaches
  // `setOpen(false)` returns true, including the FAILED save: under #11 a failed
  // PUT deliberately still closes the coach and leaves the onboarded flag
  // unwritten so it comes back, rather than trapping the user on step 3.
  // Returning `saved` here would silently strip the navigation from that
  // path. (#294)
  // A failed save of a choice the user made is said, not swallowed (E2E audit
  // 14·1, ERRORS-STATES-5): the coach still closes (#294) and comes back (#11),
  // but someone who chose Hidden must not leave believing they are hidden.
  const snack = useOptionalSnack();
  const sayNotSaved = () =>
    snack("error", "Your location privacy wasn't saved, so it hasn't changed. Check your connection and set it again in Settings.");

  const finish = async ({ savePrivacy }) => {
    // A save already in flight owns this exit. Same policy as #117's "a
    // dismissal is refused while a save is in flight". (#261)
    if (savingRef.current) return false;
    // Marked AFTER the awaited save, not before it. Marking first made a failed
    // PUT permanent: the flag is per-device localStorage and the coach never
    // reopens once it is set. (#11)
    const saved = savePrivacy ? await persistPrivacy() : true;
    if (userId && saved) markOnboarded(userId);
    if (!saved) sayNotSaved();
    track("onboarding_completed", { skipped: !savePrivacy, privacy });
    setOpen(false);
    return true;
  };

  // The X, `Dialog onClose` and Android Back all land here. Dismissing is not a
  // reason to throw away a mode the user explicitly tapped — the tiles read as a
  // live setting (2.5px coloured border on select) and there is no Save
  // affordance to tell them otherwise, so the tap IS the commit. (#11)
  const skip = async () => {
    // Re-entrancy gate. Without it a second Back press starts a SECOND
    // updateLocationPrivacy — and because the picker is still on screen and
    // still tappable, that second PUT can carry a different mode than the first,
    // leaving the user's privacy decided by whichever write the server applies
    // last. (#261)
    if (savingRef.current) return;
    const saved = await persistPrivacy();
    if (userId && saved) markOnboarded(userId);
    if (!saved) sayNotSaved();
    track("onboarding_skipped");
    setOpen(false);
  };

  // First launch is the one screen a Play reviewer is guaranteed to see, so Back
  // here must dismiss the coach rather than close the app.
  useDismissible(open, skip, "onboarding-coach");

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={skip}
      maxWidth="xs"
      fullWidth
      // Named by its own title (E2E audit 7·17 / 8·1): without a DialogTitle,
      // MUI's aria-labelledby points at nothing and a screen reader says "dialog".
      aria-labelledby="onboarding-step-title"
      PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
    >
      <Box sx={{ position: "relative", p: 3 }}>
        <IconButton
          onClick={skip}
          disabled={saving}
          size="small"
          aria-label="Skip onboarding"
          // 48dp touch target (Android minimum); the icon stays small. (F-11)
          sx={{ position: "absolute", top: 4, right: 4, minWidth: 48, minHeight: 48, color: "#94a3b8" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {step === 0 && (
          <Box sx={{ textAlign: "center", pt: 1 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #a855f7, #7c3aed)",
              }}
            >
              <PlaceIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography id="onboarding-step-title" variant="h5" fontWeight={800} gutterBottom>
              Welcome, {firstName}!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tradeloft helps you trade Pokémon TCG cards with collectors near
              you. Two quick things before you start: your location privacy and
              a few safety basics.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setStep(1)}
              sx={{ py: 1.4, borderRadius: "14px", fontWeight: 700 }}
            >
              Get started
            </Button>
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ pt: 1 }}>
            <Typography id="onboarding-step-title" variant="h6" fontWeight={800} gutterBottom>
              How should others see your location?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This is a meet-in-person app, so you're never shown at your exact
              spot. You can change this anytime from the Map.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
              {PRIVACY_CHOICES.map(({ value, label, desc, Icon, color }) => {
                // Gated on `privacyTouched`, not on `privacy` alone: `privacy`
                // is seeded "radius" and an untouched picker is no longer
                // saved, so highlighting Fuzzy on open would advertise a
                // selection this dialog will not write. Same rule the map's
                // LocationPrivacySettings already follows — "the panel cannot
                // present a guess as the user's choice". (#5)
                const active = privacyTouched ? privacy === value : storedMode === value;
                // The tiles were the only control REACHABLE DURING A SAVE with
                // no in-flight gate. (The step-0 "Get started" button has no
                // gate either, but `saving` needs `privacyTouched`, which needs
                // step 1, so step 0 cannot be on screen mid-save except via the
                // userId reset — it is unreachable, not guarded.) Every control
                // that IS reachable already carried `disabled={saving}`: the X,
                // both Continue buttons, the mission CTA and Enter Tradeloft.
                //
                // It mattered because of the (#11) rule in `skip`'s comment
                // block far above — search `(#11)`, do not count lines: the
                // tiles read as a live setting and there is no Save affordance,
                // so the tap IS the commit. A tile that repaints while a PUT
                // for a DIFFERENT mode is already in flight therefore tells the
                // user their choice was taken when it was not.
                //
                // `savingRef` rather than `saving` only for consistency with
                // `skip` and `finish`. There it is load-bearing, because
                // `dismissTopDismissible` invokes them outside React's render
                // cycle; here it is not — these are ordinary DOM handlers on
                // committed output and `saving` was measured to behave
                // identically. Do not read the ref as evidence of a race. (#293)
                const chooseIfIdle = () => {
                  if (savingRef.current) return;
                  setPrivacy(value);
                  setPrivacyTouched(true);
                };
                return (
                  <Box
                    key={value}
                    role="button"
                    tabIndex={0}
                    aria-pressed={active}
                    aria-disabled={saving}
                    onClick={chooseIfIdle}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { chooseIfIdle(); }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "14px",
                      cursor: "pointer",
                      // Belt and braces: `pointerEvents` stops the tap in a
                      // real browser, the ref gate stops the keyboard path
                      // (a focused tile still receives keydown here).
                      pointerEvents: saving ? "none" : "auto",
                      // A dead control MUST look dead. Without this the tiles
                      // are inert but visually identical, which is the same
                      // defect [#261] named on the X ("silently dead") and
                      // which the sibling test "shows the refusal on the two
                      // controls that were silent about it" exists to forbid —
                      // and `axios.defaults.timeout` is 90s, so that window is
                      // not brief. `pointerEvents: none` also makes a `cursor`
                      // branch here dead code (the Box stops being a hit
                      // target, and this ships to touch first), so the greying
                      // is the whole signal. The opacity is MUI's own disabled
                      // token, not a number chosen here — the same value the
                      // theme gives the X and both Continue buttons.
                      opacity: (t) => (saving ? t.palette.action.disabledOpacity : 1),
                      border: active ? `2.5px solid ${color}` : "2px solid rgba(0,0,0,0.08)",
                      bgcolor: active ? `${color}10` : "transparent",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: active ? `${color}18` : "rgba(0,0,0,0.05)",
                      }}
                    >
                      <Icon sx={{ fontSize: 22, color: active ? color : "#94a3b8" }} />
                    </Box>
                    <Box>
                      <Typography fontWeight={800} sx={{ fontSize: "0.9rem" }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Button
              fullWidth
              variant="contained"
              disabled={saving}
              onClick={() => setStep(2)}
              sx={{ py: 1.4, borderRadius: "14px", fontWeight: 700 }}
            >
              Continue
            </Button>
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <ShieldOutlinedIcon sx={{ color: "#6b21a8" }} />
              <Typography id="onboarding-step-title" variant="h6" fontWeight={800}>
                Trade safely
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You'll meet other collectors in person. Keep these in mind:
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
              {SAFETY_TIPS.map((tip) => (
                <Box key={tip} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box
                    sx={{
                      mt: "6px",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#6b21a8",
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2">{tip}</Typography>
                </Box>
              ))}
            </Box>

            <Button
              fullWidth
              variant="contained"
              disabled={saving}
              onClick={() => setStep(3)}
              sx={{ py: 1.4, borderRadius: "14px", fontWeight: 700 }}
            >
              Continue
            </Button>
          </Box>
        )}

        {/* The step the old sequence never had. It taught location privacy and
            safety, then dropped the user on a map that needed a wishlist to show
            anything — without ever mentioning one. Reads live account state, so
            anything already done shows as done instead of being re-taught. */}
        {step === 3 && (
          <Box sx={{ pt: 1 }} data-testid="onboarding-mission">
            <Typography id="onboarding-step-title" variant="h6" fontWeight={800} gutterBottom>
              Your first trade
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {/* Was "The map matches on your real collection and wishlist" —
                  false for every Vault card (P0 area B, skeptic). */}
              Matches come from your wishlist and the cards you list for trade
              with a photo — Vault cards aren&apos;t matched. Here&apos;s the path.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
              {(mission?.steps || []).map((s) => {
                const copy = stepCopyFor(mission, s.id) || {};
                return (
                  <Box
                    key={s.id}
                    data-testid={`mission-step-${s.id}`}
                    data-done={s.done ? "true" : "false"}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      opacity: s.done ? 0.55 : 1,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        mt: "3px",
                        width: 18,
                        height: 18,
                        flexShrink: 0,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#fff",
                        bgcolor: s.done ? "#22c55e" : "#cbd5e1",
                      }}
                    >
                      {s.done ? "✓" : ""}
                    </Box>
                    <Box>
                      <Typography
                        fontWeight={800}
                        sx={{
                          fontSize: "0.9rem",
                          textDecoration: s.done ? "line-through" : "none",
                        }}
                      >
                        {copy.title || s.label}
                      </Typography>
                      {!s.done && (
                        <Typography variant="caption" color="text.secondary">
                          {copy.body}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {nextCopy && (
              <Button
                fullWidth
                variant="contained"
                disabled={saving}
                data-testid="mission-next-cta"
                // Gated on the exit having RUN. `finish` refuses while a save is
                // in flight (#261), and navigating anyway moved the user off a
                // coach that was still on screen at that instant, on an exit
                // that recorded nothing itself. (The concurrent exit owning the
                // in-flight save — `skip` — does record it and close the coach
                // once the PUT settles, so the unrecorded state is not durable;
                // the navigation was the real defect.) `disabled={saving}` does
                // not cover it: the ref is armed synchronously by an exit
                // started elsewhere (Android Back) while this button's
                // re-render is still queued. Note the tap is now DROPPED, not
                // deferred — the user gets in-flight feedback because the
                // queued render commits to "Saving"/disabled. (#294)
                onClick={async () => {
                  // A Hidden collector's next step is their visibility, which
                  // this coach asks on step 1 — go back there rather than
                  // leave the coach for a page that would only ask again.
                  if (nextCopy.action === "open_privacy") {
                    setStep(1);
                    return;
                  }
                  if (await finish({ savePrivacy: true })) {
                    navigate(nextCopy.href);
                  }
                }}
                sx={{ py: 1.4, borderRadius: "14px", fontWeight: 700, mb: 1 }}
              >
                {saving ? "Saving…" : nextCopy.cta}
              </Button>
            )}

            <Button
              fullWidth
              disabled={saving}
              onClick={() => finish({ savePrivacy: true })}
              sx={{ py: 1.2, borderRadius: "14px", fontWeight: 700 }}
            >
              {saving ? "Saving…" : "Enter Tradeloft"}
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default OnboardingCoach;
